import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Slider,
  Tabs,
  Tab,
  Paper,
  InputAdornment,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ImageIcon from '@mui/icons-material/Image';
import PreviewIcon from '@mui/icons-material/Preview';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import { DragDropContext, Droppable, Draggable, DropResult, DroppableProvided, DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import { IPopupItem, IPopupSettings } from '../types/damTypes';
import { API_BASE_URL } from '../config';
import axiosInstance from '../services/axiosInstance';
import PopupItemEditor from './PopupItemEditor';

interface PopupSettingsModalProps {
  open: boolean;
  onClose: () => void;
  embedded?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const PopupSettingsModal: React.FC<PopupSettingsModalProps> = ({ open, onClose, embedded = false }) => {
  const [tabValue, setTabValue] = useState(0);
  const [items, setItems] = useState<IPopupItem[]>([]);
  const [settings, setSettings] = useState<IPopupSettings>({
    enabled: false,
    allowDismiss: true,
    autoAdvanceSeconds: 5
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<IPopupItem | null>(null);

  // Export function
  const handleExport = async () => {
    try {
      setExporting(true);
      setError(null);
      
      const response = await axiosInstance.post('/api/popup/export', {}, {
        responseType: 'blob'
      });
      
      // Skapa nedladdningslänk
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `popup-export-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setSuccess('Export slutförd!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Export error:', err);
      setError('Kunde inte exportera Popup');
    } finally {
      setExporting(false);
    }
  };

  // Import function
  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Återställ input så samma fil kan väljas igen
    event.target.value = '';
    
    if (!file.name.endsWith('.zip')) {
      setError('Välj en ZIP-fil');
      return;
    }
    
    if (!window.confirm('Detta kommer att importera/uppdatera Popup-data. Vill du fortsätta?')) {
      return;
    }
    
    try {
      setImporting(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axiosInstance.post('/api/popup/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSuccess(`Import slutförd! ${response.data.itemsImported} nyheter, ${response.data.filesImported} filer.`);
      setTimeout(() => setSuccess(null), 5000);
      
      // Ladda om data
      fetchData();
    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.response?.data?.message || 'Kunde inte importera Popup');
    } finally {
      setImporting(false);
    }
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, settingsRes] = await Promise.all([
        axiosInstance.get('/api/popup/items'),
        axiosInstance.get('/api/popup/settings')
      ]);
      setItems(itemsRes.data);
      setSettings(settingsRes.data);
    } catch (err: any) {
      setError('Kunde inte hämta popup-inställningar');
      console.error('Error fetching popup data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open, fetchData]);

  // Save settings
  const saveSettings = async () => {
    setSaving(true);
    setError(null);
    try {
      await axiosInstance.put('/api/popup/settings', settings);
      setSuccess('Inställningar sparade');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError('Kunde inte spara inställningar');
    } finally {
      setSaving(false);
    }
  };

  // Handle drag end for reordering
  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(items);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    setItems(reorderedItems);

    try {
      await axiosInstance.post('/api/popup/items/reorder', {
        itemIds: reorderedItems.map(item => item._id)
      });
    } catch (err) {
      console.error('Error reordering items:', err);
      fetchData(); // Reload on error
    }
  };

  // Delete item
  const handleDelete = async (itemId: string) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna nyhet?')) return;

    try {
      await axiosInstance.delete(`/api/popup/items/${itemId}`);
      setItems(items.filter(item => item._id !== itemId));
      setSuccess('Nyhet borttagen');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Kunde inte ta bort nyheten');
    }
  };

  // Toggle item active status
  const handleToggleActive = async (item: IPopupItem) => {
    try {
      await axiosInstance.put(`/api/popup/items/${item._id}`, {
        isActive: !item.isActive
      });
      setItems(items.map(i => 
        i._id === item._id ? { ...i, isActive: !i.isActive } : i
      ));
    } catch (err) {
      setError('Kunde inte uppdatera status');
    }
  };

  // Open editor
  const handleEdit = (item: IPopupItem) => {
    setEditingItem(item);
    setEditorOpen(true);
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setEditingItem(null);
  };

  const handleEditorSave = () => {
    fetchData();
    setEditorOpen(false);
    setEditingItem(null);
    setSuccess('Nyhet sparad');
    setTimeout(() => setSuccess(null), 3000);
  };

  const getItemStatus = (item: IPopupItem): { label: string; color: 'success' | 'warning' | 'error' | 'default' } => {
    if (!item.isActive) return { label: 'Inaktiv', color: 'default' };
    
    const now = new Date();
    const start = item.startDate ? new Date(item.startDate) : null;
    const end = item.endDate ? new Date(item.endDate) : null;

    if (start && start > now) return { label: 'Schemalagd', color: 'warning' };
    if (end && end < now) return { label: 'Utgången', color: 'error' };
    return { label: 'Aktiv', color: 'success' };
  };

  // Content component to reuse in both Dialog and embedded modes
  const content = (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
        <Tab label="Inställningar" />
        <Tab label="Nyheter" />
      </Tabs>

      {/* Settings Tab */}
          <TabPanel value={tabValue} index={0}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Paper sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Allmänna inställningar
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enabled}
                      onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                    />
                  }
                  label="Aktivera popup"
                  sx={{ mb: 2, display: 'block' }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.allowDismiss}
                      onChange={(e) => setSettings({ ...settings, allowDismiss: e.target.checked })}
                    />
                  }
                  label='Tillåt "Visa inte igen"'
                  sx={{ mb: 3, display: 'block' }}
                />

                <Typography variant="body2" gutterBottom>
                  Auto-bläddring (sekunder): {settings.autoAdvanceSeconds === 0 ? 'Av' : settings.autoAdvanceSeconds}
                </Typography>
                <Slider
                  value={settings.autoAdvanceSeconds}
                  onChange={(e, v) => setSettings({ ...settings, autoAdvanceSeconds: v as number })}
                  min={0}
                  max={15}
                  step={1}
                  marks={[
                    { value: 0, label: 'Av' },
                    { value: 5, label: '5s' },
                    { value: 10, label: '10s' },
                    { value: 15, label: '15s' }
                  ]}
                  sx={{ maxWidth: 400, mt: 1 }}
                />

                <Box sx={{ mt: 4 }}>
                  <Button
                    variant="contained"
                    onClick={saveSettings}
                    disabled={saving}
                  >
                    {saving ? 'Sparar...' : 'Spara inställningar'}
                  </Button>
                </Box>

                {/* Export/Import sektion */}
                <Divider sx={{ my: 4 }} />
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Export / Import
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Exportera eller importera Popup-data inklusive alla nyheter och bilder.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                    onClick={handleExport}
                    disabled={exporting || importing}
                  >
                    {exporting ? 'Exporterar...' : 'Exportera'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={importing ? <CircularProgress size={16} /> : <UploadIcon />}
                    component="label"
                    disabled={exporting || importing}
                  >
                    {importing ? 'Importerar...' : 'Importera'}
                    <input
                      type="file"
                      accept=".zip"
                      hidden
                      onChange={handleImport}
                    />
                  </Button>
                </Box>
              </Paper>
            )}
          </TabPanel>

          {/* News Items Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Nyheter ({items.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNew}
              >
                Lägg till nyhet
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : items.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <ImageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary">
                  Inga nyheter ännu. Klicka på "Lägg till nyhet" för att skapa den första.
                </Typography>
              </Paper>
            ) : (
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="popup-items">
                  {(provided: DroppableProvided) => (
                    <List {...provided.droppableProps} ref={provided.innerRef}>
                      {items.map((item, index) => {
                        const status = getItemStatus(item);
                        return (
                          <Draggable key={item._id} draggableId={item._id} index={index}>
                            {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                              <Paper
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  mb: 1,
                                  bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                                  border: '1px solid',
                                  borderColor: 'divider'
                                }}
                              >
                                <ListItem>
                                  <Box
                                    {...provided.dragHandleProps}
                                    sx={{ mr: 1, cursor: 'grab', color: 'text.secondary' }}
                                  >
                                    <DragIndicatorIcon />
                                  </Box>
                                  
                                  {/* Thumbnail */}
                                  <Box
                                    sx={{
                                      width: 60,
                                      height: 40,
                                      borderRadius: 1,
                                      overflow: 'hidden',
                                      mr: 2,
                                      flexShrink: 0,
                                      bgcolor: 'grey.200'
                                    }}
                                  >
                                    <img
                                      src={item.imageUrl.startsWith('http') 
                                        ? item.imageUrl 
                                        : `${API_BASE_URL}${item.imageUrl}`}
                                      alt=""
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                    />
                                  </Box>

                                  <ListItemText
                                    primary={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body1" fontWeight="medium">
                                          {item.title}
                                        </Typography>
                                        <Chip
                                          label={status.label}
                                          color={status.color}
                                          size="small"
                                        />
                                      </Box>
                                    }
                                    secondary={
                                      <Typography variant="body2" color="text.secondary" noWrap>
                                        {item.badgeText} • {item.bodyText?.substring(0, 50)}...
                                      </Typography>
                                    }
                                  />

                                  <ListItemSecondaryAction>
                                    <Tooltip title={item.isActive ? 'Inaktivera' : 'Aktivera'}>
                                      <Switch
                                        size="small"
                                        checked={item.isActive}
                                        onChange={() => handleToggleActive(item)}
                                      />
                                    </Tooltip>
                                    <Tooltip title="Redigera">
                                      <IconButton onClick={() => handleEdit(item)}>
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Ta bort">
                                      <IconButton onClick={() => handleDelete(item._id)} color="error">
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </ListItemSecondaryAction>
                                </ListItem>
                              </Paper>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </List>
                  )}
                </Droppable>
              </DragDropContext>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Dra för att ändra ordning. Nyheter visas i ordning från toppen.
            </Typography>
          </TabPanel>
    </>
  );

  // Embedded mode - render directly without Dialog
  if (embedded) {
    return (
      <Box sx={{ p: 0 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Popup-hantering
        </Typography>
        {content}

        {/* Item Editor Modal */}
        <PopupItemEditor
          open={editorOpen}
          item={editingItem}
          onClose={handleEditorClose}
          onSave={handleEditorSave}
        />
      </Box>
    );
  }

  // Dialog mode
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '70vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Popup-hantering</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {content}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Stäng</Button>
        </DialogActions>
      </Dialog>

      {/* Item Editor Modal */}
      <PopupItemEditor
        open={editorOpen}
        item={editingItem}
        onClose={handleEditorClose}
        onSave={handleEditorSave}
      />
    </>
  );
};

export default PopupSettingsModal;

